import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AgentRole } from '../../../models/Support/SupportEnums';
import {
	type ISupportAgentConfig,
	useAddAgentConfigMutation,
	useAgentConfigsQuery,
	useDeleteAgentConfigMutation,
	useUpdateAgentConfigMutation,
} from '../../../queries/Support/useSupportAgentConfig';
import { type IAvailableUser, useAvailableUsersQuery } from '../../../queries/Support/useSupportAvailableUsers';
import { getApiErrorMessage, getSupportUserProfilePicSrc } from '../../../utils/Support/supportHelpers';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { Avatar } from '@HoudiniUI/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { PlusIcon } from './SettingsIcons';
import { settingsStyles as st } from './styles/settingsStyles';
import { teamTabStyles as styles } from './styles/teamTabStyles';

type AgentConfigPatch = Partial<Pick<ISupportAgentConfig, 'isAvailable' | 'maxTickets' | 'role'>>;

type AgentConfigMutationPatch = {
	id: string;
	isAvailable: boolean;
	maxTickets: number | null;
	role: AgentRole;
};

export function TeamTabTeammatesSection({ mailboxId }: { mailboxId: string }) {
	const { data: agentConfigsData } = useAgentConfigsQuery(mailboxId);
	const [agentConfigMutationError, setAgentConfigMutationError] = React.useState<string | null>(null);
	const addAgentMutation = useAddAgentConfigMutation(mailboxId, {
		onSuccess: () => setAgentConfigMutationError(null),
		onError: error => setAgentConfigMutationError(getApiErrorMessage(error, 'Failed to add teammate.')),
	});
	const updateAgentMutation = useUpdateAgentConfigMutation(mailboxId, {
		onSuccess: () => setAgentConfigMutationError(null),
		onError: error => setAgentConfigMutationError(getApiErrorMessage(error, 'Failed to save teammate settings.')),
	});
	const deleteAgentMutation = useDeleteAgentConfigMutation(mailboxId, {
		onSuccess: () => setAgentConfigMutationError(null),
		onError: error => setAgentConfigMutationError(getApiErrorMessage(error, 'Failed to remove teammate.')),
	});

	const [showAddForm, setShowAddForm] = React.useState(false);
	const [searchQuery, setSearchQuery] = React.useState('');
	const debouncedSearch = useDebounceValue(searchQuery, 300);
	const trimmedSearchQuery = searchQuery.trim();
	const trimmedDebouncedSearch = debouncedSearch.trim();
	const hasSearchQuery = trimmedSearchQuery.length >= 2;
	const isSearchDebouncing = hasSearchQuery && trimmedSearchQuery !== trimmedDebouncedSearch;
	const availableUsersSearch = showAddForm && hasSearchQuery ? trimmedDebouncedSearch : '';

	const availableUsersQuery = useAvailableUsersQuery(mailboxId, availableUsersSearch);
	const agents: ISupportAgentConfig[] = agentConfigsData?.values ?? [];
	const existingIds = new Set(agents.map(a => a.agentId));
	const searchResults = (availableUsersQuery.data ?? []).filter(u => !existingIds.has(u.id));
	const isSearching = hasSearchQuery && (isSearchDebouncing || availableUsersQuery.isFetching);
	const searchError =
		hasSearchQuery && !isSearchDebouncing && availableUsersQuery.isError
			? getApiErrorMessage(availableUsersQuery.error, 'Failed to search teammates.')
			: null;
	const showSearchDropdown =
		showAddForm &&
		hasSearchQuery &&
		(isSearching || !!searchError || searchResults.length > 0 || !!availableUsersQuery.isFetched);

	const handleSelectUser = (user: IAvailableUser) => {
		setAgentConfigMutationError(null);
		addAgentMutation.mutate(
			{ agentId: user.id, agentName: getAvailableUserDisplayName(user) },
			{
				onSuccess: () => {
					setSearchQuery('');
					setShowAddForm(false);
				},
			}
		);
	};

	const handleUpdateAgent = (agent: ISupportAgentConfig, patch: AgentConfigPatch) => {
		setAgentConfigMutationError(null);
		updateAgentMutation.mutate(buildAgentConfigPatch(agent, patch));
	};

	const handleUpdateMaxTickets = (agent: ISupportAgentConfig, value: string) => {
		const parsed = parseAgentMaxTickets(value);
		if (parsed === undefined) return;
		handleUpdateAgent(agent, { maxTickets: parsed });
	};

	const [agentPendingRemoval, setAgentPendingRemoval] = React.useState<ISupportAgentConfig | null>(null);

	const confirmRemoveAgent = () => {
		if (!agentPendingRemoval) return;
		setAgentConfigMutationError(null);
		deleteAgentMutation.mutate(agentPendingRemoval.id);
		setAgentPendingRemoval(null);
	};

	return (
		<div>
			<div className={css(styles.teammatesHeader)}>
				<div className={css(styles.teammatesTitleRow)}>
					<span className={css(styles.cardSubTitle)}>Teammates</span>
					<span className={css(styles.teammatesCount)}>{agents.length}</span>
				</div>
				<Button variant='outline' size='sm' onClick={() => setShowAddForm(true)} styles={[styles.addTeammateButton]}>
					<PlusIcon />
					Add teammate
				</Button>
			</div>

			{showAddForm && (
				<div className={css(styles.addForm)}>
					<div className={css(styles.searchWrapper)}>
						<Input
							type='text'
							value={searchQuery}
							onChange={e => setSearchQuery(e.target.value)}
							placeholder='Search by name or email...'
							styles={[st.input]}
							autoFocus
						/>
						{showSearchDropdown && (
							<div className={css(styles.searchDropdown)}>
								{isSearching && <div className={css(styles.searchItem, styles.searchItemMuted)}>Searching...</div>}
								{!isSearching && searchError && (
									<div className={css(styles.searchItem, styles.searchItemMuted)}>{searchError}</div>
								)}
								{!isSearching && !searchError && searchResults.length === 0 && (
									<div className={css(styles.searchItem, styles.searchItemMuted)}>No teammates found.</div>
								)}
								{!isSearching &&
									!searchError &&
									searchResults.map(user => {
										const displayName = getAvailableUserDisplayName(user);
										return (
											<div key={user.id} className={css(styles.searchItem)} onClick={() => handleSelectUser(user)}>
												<div className={css(styles.searchResultRow)}>
													<Avatar
														imgSrc={user.profilePic}
														altText={displayName}
														fallbackText={displayName.charAt(0).toUpperCase()}
														smallText
														styles={[styles.avatarSmall]}
													/>
													<div>
														<div className={css(styles.searchResultName)}>{displayName}</div>
														<div className={css(styles.searchResultEmail)}>{user.email}</div>
													</div>
												</div>
											</div>
										);
									})}
							</div>
						)}
					</div>
					<div className={css(styles.addFormActions)}>
						<Button
							onClick={() => {
								setShowAddForm(false);
								setSearchQuery('');
								setAgentConfigMutationError(null);
							}}
						>
							Cancel
						</Button>
					</div>
				</div>
			)}

			{agentConfigMutationError && <div className={css(styles.errorMsg)}>{agentConfigMutationError}</div>}

			<div className={css(styles.agentList)}>
				{agents.length === 0 && (
					<div className={css(styles.emptyState)}>No teammates added yet. Click "Add Teammate" to get started.</div>
				)}
				{agents.map(agent => (
					<AgentRow
						key={agent.id}
						agent={agent}
						onToggleAvailability={() => handleUpdateAgent(agent, { isAvailable: !agent.isAvailable })}
						onUpdateMaxTickets={value => handleUpdateMaxTickets(agent, value)}
						onUpdateRole={role => handleUpdateAgent(agent, { role })}
						onRemove={() => setAgentPendingRemoval(agent)}
					/>
				))}
			</div>

			{agentPendingRemoval && (
				<ConfirmModal
					title='Remove Teammate'
					message={`Are you sure you want to remove ${getAgentDisplayName(agentPendingRemoval)} from this mailbox? Tickets assigned to them will stay assigned.`}
					confirmLabel='Remove'
					variant='danger'
					onConfirm={confirmRemoveAgent}
					onCancel={() => setAgentPendingRemoval(null)}
				/>
			)}
		</div>
	);
}

function getAvailableUserDisplayName(user: IAvailableUser) {
	return user.name || user.email || 'Unknown teammate';
}

// Some API agent configs omit both the name and ID.
function getAgentDisplayName(agent: ISupportAgentConfig) {
	return agent.agentName || agent.agentId || 'Unknown teammate';
}

function AgentRow({
	agent,
	onToggleAvailability,
	onUpdateMaxTickets,
	onUpdateRole,
	onRemove,
}: {
	agent: ISupportAgentConfig;
	onToggleAvailability: () => void;
	onUpdateMaxTickets: (value: string) => void;
	onUpdateRole: (role: AgentRole) => void;
	onRemove: () => void;
}) {
	const userSession = useAdminUserSession();
	const displayName = getAgentDisplayName(agent);
	const initial = displayName.charAt(0).toUpperCase();
	const isCurrentUser = agent.agentId === userSession.user.id;
	const avatarImgSrc = getSupportUserProfilePicSrc({
		agentProfilePic: agent.agentProfilePic,
		profilePic: isCurrentUser ? userSession.user.profilePic : undefined,
		userId: agent.agentId,
		accountId: userSession.account?.id,
	});

	return (
		<div className={css(styles.agentRow)}>
			<div className={css(styles.agentInfo)}>
				<Avatar imgSrc={avatarImgSrc} altText={displayName} fallbackText={initial} />
				<span className={css(styles.agentName)}>{displayName}</span>
			</div>
			<div className={css(styles.agentControls)}>
				<label className={css(styles.availabilityLabel)}>
					<Button
						type='button'
						variant='ghost'
						styles={[st.toggle, styles.toggleSmall, agent.isAvailable && st.toggleActive]}
						onClick={onToggleAvailability}
						aria-pressed={agent.isAvailable}
						aria-label={`Toggle availability for ${displayName}`}
					>
						<span
							className={css(
								st.toggleKnob,
								styles.toggleKnobSmall,
								agent.isAvailable && st.toggleKnobActive,
								agent.isAvailable && styles.toggleKnobSmallActive
							)}
						/>
					</Button>
					<span className={css(styles.availabilityText)}>{agent.isAvailable ? 'Available' : 'Unavailable'}</span>
				</label>
				<Select value={agent.role || AgentRole.Support} onValueChange={value => onUpdateRole(value as AgentRole)}>
					<SelectTrigger styles={[st.selectPill, styles.roleSelectTrigger]} title='Agent role'>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={AgentRole.Support}>Support</SelectItem>
						<SelectItem value={AgentRole.Admin}>Admin</SelectItem>
					</SelectContent>
				</Select>
				<Input
					type='number'
					min={1}
					value={agent.maxTickets ?? ''}
					onChange={e => onUpdateMaxTickets(e.target.value)}
					placeholder='Max'
					styles={[styles.numberInput, styles.numberInputSmall]}
					title='Max active tickets override'
				/>
				<Button variant='ghost' size='sm' onClick={onRemove} title={`Remove ${displayName}`}>
					&times;
				</Button>
			</div>
		</div>
	);
}

function buildAgentConfigPatch(agent: ISupportAgentConfig, patch: AgentConfigPatch): AgentConfigMutationPatch {
	return {
		id: agent.id,
		isAvailable: patch.isAvailable ?? agent.isAvailable,
		maxTickets: patch.maxTickets !== undefined ? patch.maxTickets : (agent.maxTickets ?? null),
		role: patch.role ?? agent.role ?? AgentRole.Support,
	};
}

function parseAgentMaxTickets(value: string) {
	if (value === '') return null;

	const parsed = parseInt(value, 10);
	if (isNaN(parsed) || parsed < 0) return undefined;

	return parsed;
}
